import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Users, MapPin, ClipboardCheck } from 'lucide-react';

interface ManualRoutingStepsProps {
  currentStep: number;
  onStepChange: (step: number) => void;
  totalAssignments: number;
  totalRecalls: number;
  children: React.ReactNode;
}

const ManualRoutingSteps: React.FC<ManualRoutingStepsProps> = ({
  currentStep,
  onStepChange,
  totalAssignments,
  totalRecalls,
  children
}) => {
  const steps = [
    {
      id: 1,
      title: 'Assign Technician',
      icon: Users,
      description: 'Assign technicians to selected recalls'
    },
    {
      id: 2,
      title: 'Confirm Routes',
      icon: MapPin,
      description: 'Review and confirm technician routes'
    }
  ];

  return (
    <div className='w-full h-full flex flex-col space-y-4'>
      {/* Step Navigation Header */}
      <div className='bg-card rounded-lg border p-4 lg:p-6 flex-shrink-0'>
        <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4'>
          <div className='flex items-center gap-3'>
            <ClipboardCheck className='h-6 w-6 text-primary' />
            <div>
              <h1 className='text-2xl font-bold tracking-tight'>Manual Routing</h1>
              <p className='text-muted-foreground text-sm'>
                Assign technicians to recalls and confirm routes
              </p>
            </div>
          </div>
          <div className='flex items-center gap-2 lg:gap-4'>
            {totalRecalls > 0 ? (
              <>
                <Badge variant='outline' className='text-sm'>
                  {totalRecalls} Unconfirmed Recall{totalRecalls !== 1 ? 's' : ''}
                </Badge>
                {totalAssignments > 0 && (
                  <Badge
                    variant='outline'
                    className={[
                      'text-sm bg-green-500/20 text-green-400 border-green-500/30',
                      'dark:bg-green-500/30 dark:text-green-300 dark:border-green-400/30'
                    ].join(' ')}
                  >
                    {totalAssignments} Assignment{totalAssignments !== 1 ? 's' : ''}
                  </Badge>
                )}
              </>
            ) : (
              <Badge
                variant='outline'
                className={[
                  'text-sm bg-blue-500/20 text-blue-400 border-blue-500/30',
                  'dark:bg-blue-500/30 dark:text-blue-300 dark:border-blue-400/30'
                ].join(' ')}
              >
                All Recalls Confirmed
              </Badge>
            )}
          </div>
        </div>

        {/* Step Navigation */}
        <div className='flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8'>
          {steps.map((step, index) => {
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            const Icon = step.icon;

            return (
              <div key={step.id} className='flex flex-col items-center'>
                <div className='flex items-center space-x-3'>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      isActive
                        ? 'bg-green-500 text-white'
                        : isCompleted
                          ? 'bg-green-500/20 text-green-400 dark:bg-green-500/30 dark:text-green-300'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className='w-5 h-5' />
                    ) : (
                      <Icon className='w-4 h-4' />
                    )}
                  </div>
                  <div className='text-left'>
                    <div
                      className={`text-sm font-medium ${
                        isActive
                          ? 'text-green-600 dark:text-green-400'
                          : isCompleted
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {step.id}. {step.title}
                    </div>
                    <div className='text-xs text-muted-foreground'>{step.description}</div>
                  </div>
                </div>

                {/* Step Line */}
                {index < steps.length - 1 && (
                  <div
                    className={`w-16 h-0.5 mt-4 ${
                      currentStep > step.id ? 'bg-green-500 dark:bg-green-400' : 'bg-border'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Progress Bar */}
        <div className='mt-6'>
          <div className='w-full bg-border rounded-full h-2'>
            <div
              className='bg-green-500 dark:bg-green-400 h-2 rounded-full transition-all duration-300'
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className='flex-1 min-h-0 overflow-auto w-full'>{children}</div>

      {/* Step Navigation Buttons */}
      <div
        className={[
          'flex flex-col sm:flex-row justify-between items-center',
          'bg-card rounded-lg border p-4 gap-4 flex-shrink-0'
        ].join(' ')}
      >
        <div>
          {currentStep > 1 && (
            <Button
              variant='outline'
              onClick={() => onStepChange(currentStep - 1)}
              className='flex items-center gap-2'
            >
              ← Previous
            </Button>
          )}
        </div>

        <div className='flex items-center gap-2'>
          <span className='text-sm text-muted-foreground'>
            Step {currentStep} of {steps.length}
          </span>
        </div>

        <div>
          {currentStep < steps.length && (
            <Button
              onClick={() => onStepChange(currentStep + 1)}
              disabled={currentStep === 1 && totalAssignments === 0}
              className='flex items-center gap-2'
            >
              Next →
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualRoutingSteps;
